import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { getRealDate } from '@lmt/unicorn-shared/app/helpers/timezone-change-helper';
import clsx from 'clsx';
import React, { useState } from 'react';
import StartErrorPage from '@lmt/unicorn-shared/app/components/screen-plugs/StartErrorPage';
import { useTranslation } from 'react-i18next';
import {
  browserVersion,
  isChrome,
  isChromium,
  isEdgeChromium,
  isElectron,
  isFirefox,
  isOpera,
  isSafari,
} from 'react-device-detect';

import LogoIcon from '@lmt/unicorn-shared/app/icons/logo-title-blue.svg?react';
import RainbowIcon from '@lmt/unicorn-shared/app/icons/rainbow.svg?react';
import style from './StartErrorInspector.module.scss';
import { logOut } from '../../features/auth/operations';
import { Loading } from '../common/Loading';

/**
 * Wrapper (HOC) for App component to allow only supported browsers and only one application tab open in a browser
 * Based on https://github.com/TheBrainFamily/react-one-tab-enforcer
 */
export function withStartErrorCheck<T extends {}>({
  StartErrorComponent = StartErrorPage,
  localStorageTimeout = 15 * 1000, // 15,000 milliseconds = 15 seconds.
  localStorageResetInterval = 10 * 1000, // 10,000 milliseconds = 10 seconds.
  localStorageTabKey = 'lmpm-active-tabs', // has to be unique!
} = {}) {
  return (WrappedComponent: React.FC<T>) => {
    // ...and returns another component...
    return (props: T) => {
      const i18next = useTranslation();
      const [isLoading, setIsLoading] = useState(false);
      if (isBrowserOutdated()) {
        return (
          <StartErrorComponent
            title={i18next.t('screen_plugs.outdated_browser_title')}
            text={i18next.t('screen_plugs.outdated_browser_text')}
            logoIconComponent={
              <LogoIcon className={clsx(style.greyed, style.logo)} />
            }
            rainbowIconComponent={<RainbowIcon className={style.greyed} />}
          />
        );
      } else if (
        isDuplicatedWindow(
          localStorageTimeout,
          localStorageResetInterval,
          localStorageTabKey,
        )
      ) {
        return (
          <Loading loading={isLoading} flex={true}>
            <StartErrorComponent
              title={i18next.t('screen_plugs.only_one_tab_title')}
              text={i18next.t('screen_plugs.only_one_tab_text')}
              logoIconComponent={<LogoIcon className={style.logo} />}
              rainbowIconComponent={<RainbowIcon />}
              button={{
                title: i18next.t('screen_plugs.switch_to_current_tab'),
                onClick: async () => {
                  try {
                    setIsLoading(true);
                    await switchToCurrentTab(localStorageTabKey);
                  } finally {
                    setIsLoading(false);
                  }
                },
              }}
            />
          </Loading>
        );
      } else {
        return <WrappedComponent {...props} />;
      }
    };
  };
}

async function switchToCurrentTab(key: string) {
  await logOut();

  localStorage.removeItem(key);
  // document.location = '/';
}

const isDuplicatedWindow = (
  localStorageTimeout: number,
  localStorageResetInterval: number,
  localStorageTabKey: string,
) => {
  if (isElectron) {
    return false;
  }

  /**
   * Compare our tab identifier associated with this session (particular tab)
   * with that of one that is in window name Storage (the active one for this browser).
   * This browser tab is good if any of the following are true:
   * 1.  There is no Storage Guid yet (first browser tab).
   * 2.  The window name matches the Storage Guid.  Same tab, refreshed.
   * 3.  The window name Storage timeout period has ended.
   *
   * If our current session is the correct active one, an interval will continue
   * to re-insert the window name Storage value with an updated timestamp.
   *
   * Another thing, that should be done (so you can open a tab within 15 seconds of closing it) would be to do the following (or hook onto an existing onunload method):
   */
  const isTabDuplicated = () => {
    const sessionId = window.name || createId();
    window.name = sessionId;

    const value = localStorage.getItem(localStorageTabKey);
    const activeTab = value ? JSON.parse(value) : null;

    // If no or stale tab object, our session is the winner.  If the guid matches, ours is still the winner
    if (
      activeTab === null ||
      activeTab.timestamp < getRealDate().getTime() - localStorageTimeout ||
      activeTab.guid === sessionId
    ) {
      const setActiveTab = () => {
        const newTab = {
          guid: sessionId,
          timestamp: getRealDate().getTime(),
        };
        localStorage.setItem(localStorageTabKey, JSON.stringify(newTab));
      };

      setActiveTab();
      setInterval(setActiveTab, localStorageResetInterval); //every x interval refresh timestamp in local storage
      window.onunload = () => {
        localStorage.removeItem(localStorageTabKey);
      };
      window.onstorage = (e) => {
        if (e.key === localStorageTabKey) {
          // reload the other tabs
          document.location = '/';
        }
      };
      return false;
    } else {
      // An active tab is already open that does not match our session guid.
      return true;
    }
  };
  return isTabDuplicated();
};

const isBrowserOutdated = (): boolean => {
  if (isElectron) {
    return false;
  }
  if (isChrome) {
    return Number(browserVersion) < 84;
  } else if (isFirefox) {
    return Number(browserVersion) < 80;
  } else if (isOpera) {
    return Number(browserVersion) < 70;
  } else if (isEdgeChromium) {
    return Number(browserVersion) < 84;
  } else if (isSafari) {
    return Number(browserVersion) < 14;
  } else if (isChromium) {
    return Number(browserVersion) < 84;
  }
  // if it's some different browser but it's chromium-based we allow it to use the app
  const isChromiumBased = navigator.userAgent.includes('Chrome');
  return !isChromiumBased;
};
